import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { generateMockData } from "../Graphql/MockData";
import { GET_MY_CART_ITEMS } from "../Graphql/cartQueries";
import client from "../Graphql/apolloClient";
import { differenceInCalendarDays, format } from 'date-fns';
import { CART_PAGE_ITEM } from '@/Graphql/Queries';
// import { isValidEmail, isValidPhoneNumber, isValidFullName } from './validationUtils'; // Uncomment if needed

export interface CartItem {
  id: string; // Allow both numbers and strings for deals
  name: string;
  location: string;
  state: string;
  price: number;
  impressions: number;
  uniqueVisitors: number;
  frequency: number;
  size: string;
  type: string;
  mediaOwner?: string;
  status: "available" | "sold-out";
  image: string;
  spots?: number; // Number of spots per day for digital billboards
  campaignDuration?: number; // Campaign duration in days (default 30)
  dailyHours?: number; // Daily operating hours (15-18 hours, default 18)
  isDeal?: boolean; // Flag to identify deal items
  daypartSchedules?: string[]; // Selected daypart schedules for digital billboards
  // Package/Deal specific dates
  packageStartDate?: string; // Fixed start date for packages/deals
  packageEndDate?: string; // Fixed end date for packages/deals
  requiresCampaignDuration?: boolean; // Whether this inventory type requires user-defined campaign duration
  // Full package data for API synchronization
  packageDetails?: any; // Complete package object for ADD_CAMPAIGN_CART API
  currencyCode?: string; // Currency code from billboard data (e.g., "INR", "USD")
  countryId?: string; // Country ID from selected country
  countryName?: string; // Country name from selected country
  // New API fields for package pricing
  totalPrice?: number; // Total package price from new API response
  validityStartDate?: string; // Package validity start date from new API response
  validityEndDate?: string; // Package validity end date from new API response
  specifications: {
    resolution: string;
    duration: string;
  };
  priceFormat: 'spot-based' | 'day-based';
  isSpotBased?: boolean; // Derived from priceFormat for easier checks
  perSpotPrice?: number;
  perDayPrice?: number;
  startDate?: string; // Start date for the billboard campaign
  endDate?: string; // End date for the billboard campaign
}


interface CartContextType {
  cartItems: CartItem[];
  addToCart: (item: CartItem) => void;
  removeFromCart: (id: number | string) => void;
  clearCart: () => void;
  isInCart: (id: number | string) => boolean;
  cartCount: number;
  totalCost: number;
  totalImpressions: number;
  totalUniqueReach: number;
  totalAverageFrequency: number;
  updateSpots: (id: string, spots: number) => void; // Only for digital billboards
  updateCampaignSettings: (id: string, duration: number, hours: number) => void; // Only for billboards 
  updateCampaignDuration: (id: string, duration: number) => void; // Only for billboards
  updateDaypartSchedules: (id: string, schedules: string[]) => void; // Only for digital billboards
  // Function to calculate total cost with custom campaign duration (for non-logged-in users)
  calculateTotalWithDuration: (customDuration: number) => number;
  // Current campaign duration override for non-logged-in users
  currentCampaignDuration: number | null;
  setCurrentCampaignDuration: (duration: number | null) => void;
  // Tax information from selected country
  taxLabel: string; // "GST", "SST", "VAT", etc.
  taxPercent: number; // 18.0, 6.0, etc.
  updateTaxInfo: (label: string, percent: number) => void; // Update tax info when country changes
  // Cart country management (for explore page country locking)
  cartCountry: string; // Country ID when cart has items (e.g., "philippines", "malaysia")
  setCartCountry: (country: string) => void; // Set cart country when first item is added
  cartCountryId: string; // Country ID when cart has items (e.g., "philippines", "malaysia")
  setCartCountryId: (country: string) => void;
  clearCartCountry: () => void; // Clear cart country when cart becomes empty
  hasCartCountry: () => boolean; // Check if cart has a locked country
  // New helper functions for dynamic inventory handling
  requiresCampaignDates: () => boolean; // Check if cart contains items requiring manual date selection
  getAutomaticCampaignDates: () => { startDate: string; endDate: string } | null; // Get auto-determined dates for packages/deals
  hasInventoryTypeMix: () => boolean; // Check if cart has mixed inventory types
  getInventoryTypeBreakdown: () => { digital: number; classic: number; packages: number; deals: number; other: number };
  getInventoryTypeProperties: (type: string, packageItem?: CartItem) => any; // Get package/deal properties with dates
  fetchCartDetails: () => Promise<void>;
  setCartItems: (items: CartItem[]) => void;
  updateCartFromApi: (apiInventories: any[]) => void;
  setCartItemCount: (count: number) => void;
  priceFormat: 'spot-based' | 'day-based';
  setPriceFormat: (format: 'spot-based' | 'day-based') => void;
  // Date management for cart campaigns
  startDate: Date | null;
  endDate: Date | null;
  updateStartDate: (date: Date | null) => void;
  updateEndDate: (date: Date | null) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: ReactNode }) {
  const [cartItems, setCartItemsRaw] = useState<CartItem[]>([]);
  const [priceFormat, setPriceFormat] = useState<'spot-based' | 'day-based'>('day-based');
  const [currentCampaignDuration, setCurrentCampaignDuration] = useState<number | null>(null);

  // Helper to deduplicate by id
  function dedupeCartItems(items: CartItem[]): CartItem[] {
    console.log("items before dedupe:", items);

    const seen = new Set();
    return items.filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  }

  // Always deduplicate when setting cartItems
  const setCartItems = (items: CartItem[]) => {
    console.log("setCartItems function called in context:", items);
    setCartItemsRaw(dedupeCartItems(items));
    console.log("after dupecartitem-->", cartItems);
  };
  const { user } = useAuth();
  const [cartCount, setCartCount] = useState(0);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Tax information from selected country
  const [taxLabel, setTaxLabel] = useState<string>('Tax');
  const [taxPercent, setTaxPercent] = useState<number>(0);

  // Cart country management
  const [cartCountry, setCartCountryState] = useState<string>('');
  const [cartCountryId, setCartCountryStateId] = useState<string>('');

  // Initialize dates as null (blank) on page load
  // Dates will be retained from localStorage only if already set in the session
  const [startDate, setStartDateState] = useState<Date | null>(null);
  const [endDate, setEndDateState] = useState<Date | null>(null);


  // Load cart country from localStorage on mount
  useEffect(() => {
    const savedCartCountry = localStorage.getItem('cartCountry');
    if (savedCartCountry) {
      setCartCountryState(savedCartCountry);
    }
  }, []);

  // Automatically clear cart country when cart becomes empty
  useEffect(() => {
    console.log('cart length:', cartItems.length, 'cartCountry:', cartCountry, 'cartItem:', cartItems, 'cartCountryId:', cartCountryId);

    if (cartItems.length === 0 && cartCountry) {
      console.log('🔄 Cart is empty - clearing cart country');
      clearCartCountry();
    }
  }, [cartItems.length, cartCountry]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('billboardCart');
    if (savedCart) {
      try {
        const cartData = JSON.parse(savedCart);

        // Version check to clear old cart items that may have incorrect types
        const cartVersion = localStorage.getItem('cartVersion');
        if (cartVersion && cartVersion !== '2.0') {
          // Only clear if there's an existing version that's different
          // Clear old cart and set new version
          localStorage.setItem('cartVersion', '2.0');
          localStorage.removeItem('billboardCart');
          setCartItems([]);
        } else {
          // If no version exists or version is correct, load the cart and set version
          if (!cartVersion) {
            localStorage.setItem('cartVersion', '2.0');
          }

          // Migrate existing cart items to ensure proper pricing fields and campaignDuration
          const migratedCartData = cartData.map((item: CartItem) => {
            let migratedItem = { ...item };

            // Ensure campaignDuration is set
            if (!migratedItem.campaignDuration) {
              migratedItem.campaignDuration = 1; // Default to 1 day
            }

            // Ensure pricing fields are set
            if (item?.type?.toLowerCase() === "digital" && !migratedItem.perSpotPrice) {
              migratedItem.perSpotPrice = item.price / 100;
            } else if (item?.type?.toLowerCase() === "classic" && !migratedItem.perDayPrice) {
              migratedItem.perDayPrice = (item.price / 100) / 30;
            }

            return migratedItem;
          });

          setCartItems(migratedCartData);
        }
      } catch (error) {
        console.error('Error loading cart from localStorage:', error);
      }
    } else {
      // Set version even if no cart exists
      const cartVersion = localStorage.getItem('cartVersion');
      if (!cartVersion) {
        localStorage.setItem('cartVersion', '2.0');
      }
    }

    // Load tax info from localStorage
    const savedTaxInfo = localStorage.getItem('cartTaxInfo');
    if (savedTaxInfo) {
      try {
        const taxData = JSON.parse(savedTaxInfo);
        setTaxLabel(taxData.label || 'N/A');
        setTaxPercent(taxData.percent || 0);
      } catch (error) {
        console.error('Error loading tax info from localStorage:', error);
      }
    }

    // Load and validate dates from localStorage (for navigation back to page)
    // Only load if dates were previously set in this session
    const storedStart = localStorage.getItem('cart_start_date');
    const storedEnd = localStorage.getItem('cart_end_date');
    const today = new Date();

    let parsedStart: Date | null = null;
    let parsedEnd: Date | null = null;

    if (storedStart && storedEnd) {
      try {
        parsedStart = new Date(storedStart);
        parsedEnd = new Date(storedEnd);
        // Validate: Not past, start ≤ end
        if (parsedStart >= today && parsedEnd >= parsedStart) {
          // Valid - load the dates
          setStartDateState(parsedStart);
          setEndDateState(parsedEnd);
        } else {
          // Invalid - clear stored dates
          localStorage.removeItem('cart_start_date');
          localStorage.removeItem('cart_end_date');
        }
      } catch (error) {
        console.error('[CONTEXT] Error parsing stored dates:', error);
        localStorage.removeItem('cart_start_date');
        localStorage.removeItem('cart_end_date');
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('billboardCart', JSON.stringify(cartItems));
  }, [cartItems]);

  // Clear dates when cart becomes empty
  useEffect(() => {
    if (cartItems.length === 0) {
      // Clear dates from state and localStorage when cart is empty
      setStartDateState(null);
      setEndDateState(null);
      localStorage.removeItem('cart_start_date');
      localStorage.removeItem('cart_end_date');
    }
  }, [cartItems.length]);

  // Clear cart when user logs out
  useEffect(() => {
    if (!user) {
      // Don't clear cart for non-logged-in users to preserve their selections
      // setCartItems([]);
    }
  }, [user]);

  // Check auth status when the component mounts
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    setIsAuthenticated(!!token);

    // If mock data is enabled, we're always "authenticated" regardless of token
    if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
      setIsAuthenticated(true);
    }
  }, []);

  // Update cartCount whenever cartItems changes
  useEffect(() => {
    setCartCount(cartItems.length);
    // Dispatch event for other components
    window.dispatchEvent(new Event('cartUpdated'));
  }, [cartItems]);

  // Calculate total cost - this will be recalculated when cartItems or currentCampaignDuration change
  const totalCost = useMemo(() => cartItems.reduce((sum, item) => {
    // Deals have fixed pricing - no duration calculation
    if (item.isDeal || item.type === "Deal") {
      return sum + (item.price / 100); // Fixed deal price
    }

    // Use currentCampaignDuration if available (for non-logged-in users), otherwise use item's campaignDuration
    const effectiveDuration = currentCampaignDuration || item.campaignDuration || 1;

    // For digital billboards, use perSpotPrice * campaignDuration * spots
    if (item?.type?.toLowerCase() === "digital") {
      const spotPrice = item?.perSpotPrice ?? (item?.price / 100);
      const spots = item.spots ?? 1;
      const isSpotBased = item?.isSpotBased || item?.priceFormat === 'spot-based';

      if (isSpotBased) {
        const totalCost = spotPrice * spots * effectiveDuration;
        return sum + totalCost;
      } else {
        const totalCost = spotPrice * effectiveDuration;
        return sum + totalCost;
      }
    }

    // For classic billboards, use perDayPrice * campaignDuration
    if (item?.type?.toLowerCase() === "classic") {
      const dayPrice = item?.perDayPrice ?? 0; // Use 0 if not set instead of fallback
      const totalCost = dayPrice * effectiveDuration;
      return sum + totalCost;
    }

    // For packages, calculate based on current campaign duration
    if (item?.type?.toLowerCase() === "package") {
      const totalPrice = item.price / 100;

      // If package has validity dates, calculate daily price and multiply by effective duration
      if (item.packageStartDate && item.packageEndDate) {
        const packageDuration = Math.floor(
          (new Date(item.packageEndDate).getTime() - new Date(item.packageStartDate).getTime())
          / (1000 * 60 * 60 * 24)
        ) + 1;
        const oneDayPrice = totalPrice / packageDuration;

        // Use effective duration, but cap at package validity
        const useDuration = Math.min(effectiveDuration, packageDuration);
        const finalPrice = oneDayPrice * useDuration;

        return sum + finalPrice;
      } else {
        // If no validity dates, use fixed package price
        console.log('Package pricing (no dates):', { totalPrice, itemPrice: item.price });
        return sum + totalPrice;
      }
    }

    // For other types, multiply by duration
    const totalCost = (item.price / 100) * effectiveDuration;
    return sum + totalCost;
  }, 0), [cartItems, currentCampaignDuration]);

  // Force totalCost recalculation when currentCampaignDuration changes
  useEffect(() => {
    // This effect forces a re-render which recalculates totalCost
  }, [currentCampaignDuration, totalCost]);

  const totalImpressions = cartItems.reduce((sum, item) => sum + item.impressions, 0);
  // Calculate total unique reach and average frequency from cart items
  const totalUniqueReach = cartItems.length > 0 ? cartItems.reduce((sum, item) => sum + item.uniqueVisitors, 0) : 0;
  const totalAverageFrequency = cartItems.length > 0
    ? cartItems.reduce((sum, item) => sum + (item.frequency || 0), 0) / cartItems.length
    : 0;


  const updateSpots = (id: string, spots: number) => {
    setCartItemsRaw((prev: CartItem[]) => prev.map((item: CartItem) =>
      item.id === id ? { ...item, spots: Math.max(1, spots) } : item
    ));
  };

  const updateCampaignSettings = (id: string, duration: number, hours: number) => {
    setCartItemsRaw((prev: CartItem[]) => prev.map((item: CartItem) => {
      if (item.id === id) {
        const minDuration = item.type === "Classic" ? 30 : 1;
        return {
          ...item,
          campaignDuration: Math.max(minDuration, duration),
          dailyHours: Math.max(15, Math.min(18, hours))
        };
      }
      return item;
    }));
  };

  const updateCampaignDuration = (id: string, duration: number) => {
    setCartItemsRaw((prev: CartItem[]) => prev.map((item: CartItem) => {
      if (item.id === id) {
        const minDuration = item.type === "Classic" ? 30 : 1;
        return {
          ...item,
          campaignDuration: Math.max(minDuration, duration)
        };
      }
      return item;
    }));
  };

  const updateDaypartSchedules = (id: string, schedules: string[]) => {
    setCartItemsRaw((prev: CartItem[]) => prev.map((item: CartItem) => {
      if (item.id === id && item.type?.toLowerCase() === "digital") {
        return {
          ...item,
          daypartSchedules: schedules
        };
      }
      return item;
    }));
  };

  // Date update functions with validation and price recalculation
  const updateStartDate = (date: Date | null) => {
    // Allow null to clear the date
    if (date === null) {
      setStartDateState(null);
      localStorage.removeItem('cart_start_date');
      return;
    }

    const today = new Date();
    if (date < today) date = today; // Prevent past dates
    setStartDateState(date);
    localStorage.setItem('cart_start_date', format(date, 'yyyy-MM-dd'));
    
    // Recalculate prices for all items when start date changes
    if (endDate) {
      const days = differenceInCalendarDays(endDate, date) + 1;
      setCartItemsRaw((prev: CartItem[]) => prev.map((item: CartItem) => {
        // Update campaign duration for billboards
        if (item.type?.toLowerCase() === "digital" || item.type?.toLowerCase() === "classic") {
          return {
            ...item,
            campaignDuration: Math.max(1, days),
            startDate: format(date, 'dd MMMM yyyy'),
            endDate: format(endDate, 'dd MMMM yyyy')
          };
        }
        return item;
      }));
    }
  };

  const updateEndDate = (date: Date | null) => {
    if (!date) {
      setEndDateState(null);
      localStorage.removeItem('cart_end_date');
      return;
    }
    const today = new Date();
    if (date < today) date = today; // Prevent past dates
    setEndDateState(date);
    localStorage.setItem('cart_end_date', format(date, 'yyyy-MM-dd'));

    // If end is before start, set start to end
    if (startDate && date < startDate) {
      setStartDateState(date);
      localStorage.setItem('cart_start_date', format(date, 'yyyy-MM-dd'));
    }
    
    // Recalculate prices for all items when end date changes
    if (startDate) {
      const days = differenceInCalendarDays(date, startDate) + 1;
      setCartItemsRaw((prev: CartItem[]) => prev.map((item: CartItem) => {
        // Update campaign duration for billboards
        if (item.type?.toLowerCase() === "digital" || item.type?.toLowerCase() === "classic") {
          return {
            ...item,
            campaignDuration: Math.max(1, days),
            startDate: format(startDate, 'dd MMMM yyyy'),
            endDate: format(date, 'dd MMMM yyyy')
          };
        }
        return item;
      }));
    }
  };

  // Helper function to determine if inventory type requires campaign duration
  const getInventoryTypeProperties = (type: string, packageItem?: CartItem) => {
    type = type?.toLowerCase();
    switch (type) {
      case "Digital":
      case "Classic":
        return { requiresCampaignDuration: true };
      case "Package":
      case "Deal":
        return {
          requiresCampaignDuration: false,
          // Use actual package dates if available, otherwise fall back to defaults
          packageStartDate: packageItem?.packageStartDate || "",
          packageEndDate: packageItem?.packageEndDate || ""
        };
      default:
        return { requiresCampaignDuration: false };
    }
  };

  // Helper functions for dynamic inventory handling
  const requiresCampaignDates = () => {
    return cartItems.some(item => {
      const props = getInventoryTypeProperties(item.type, item);
      return props.requiresCampaignDuration;
    });
  };

  const getAutomaticCampaignDates = () => {
    const packagesAndDeals = cartItems.filter(item =>
      item.type.toLowerCase() === "package" || item.type.toLowerCase() === "deal"
    );

    if (packagesAndDeals.length === 0) return null;

    // Find the latest start date and end date among all packages/deals
    let latestStartDate = "";
    let latestEndDate = "";

    packagesAndDeals.forEach(item => {
      const props = getInventoryTypeProperties(item.type, item);
      if (props.packageStartDate && (!latestStartDate || props.packageStartDate > latestStartDate)) {
        latestStartDate = props.packageStartDate;
      }
      if (props.packageEndDate && (!latestEndDate || props.packageEndDate > latestEndDate)) {
        latestEndDate = props.packageEndDate;
      }
    });

    return latestStartDate && latestEndDate ?
      { startDate: latestStartDate, endDate: latestEndDate } : null;
  };

  const hasInventoryTypeMix = () => {
    const breakdown = getInventoryTypeBreakdown();
    const nonZeroTypes = Object.values(breakdown).filter(count => count > 0).length;
    return nonZeroTypes > 1;
  };

  const getInventoryTypeBreakdown = () => {
    return cartItems.reduce((breakdown, item) => {
      switch (item.type) {
        case "Digital":
          breakdown.digital++;
          break;
        case "Classic":
          breakdown.classic++;
          break;
        case "Package":
          breakdown.packages++;
          break;
        case "Deal":
          breakdown.deals++;
          break;
        default:
          breakdown.other++;
          break;
      }
      return breakdown;
    }, { digital: 0, classic: 0, packages: 0, deals: 0, other: 0 });
  };

  const addToCart = (item: CartItem) => {
    // Note: For unauthenticated users, allow cart additions but store locally
    // The login redirect logic is handled at checkout time

    setCartItemsRaw((prev: CartItem[]) => {
      const typeProps = getInventoryTypeProperties(item.type, item);

      // Ensure pricing fields are properly set for calculations
      let itemWithPricing = { ...item };

      // Set perSpotPrice and perDayPrice based on item type
      if (item?.type?.toLowerCase() === "digital") {
        // For digital billboards, ensure perSpotPrice is set
        itemWithPricing.perSpotPrice = item.perSpotPrice || (item.price / 100);
      } else if (item?.type?.toLowerCase() === "classic") {
        // For classic billboards, convert monthly price to daily price
        // If perDayPrice is not provided, calculate it from monthly price
        itemWithPricing.perDayPrice = item.perDayPrice || ((item.price / 100) / 30);
      }

      // Calculate campaign duration from start and end dates if provided
      let calculatedDuration = item.campaignDuration || 1;
      if (item.startDate && item.endDate) {
        try {
          const start = new Date(item.startDate);
          const end = new Date(item.endDate);
          const days = differenceInCalendarDays(end, start) + 1;
          calculatedDuration = Math.max(1, days);
        } catch (error) {
          console.error('Error calculating campaign duration from dates:', error);
        }
      }

      const itemWithDefaults = item.type === "Digital" ? {
        ...itemWithPricing,
        ...typeProps,
        spots: 1,
        campaignDuration: calculatedDuration,
        dailyHours: 18,
        daypartSchedules: ["Morning", "Afternoon", "Evening"],
        startDate: item.startDate,
        endDate: item.endDate
      } : item.type === "Classic" ? {
        ...itemWithPricing,
        ...typeProps,
        campaignDuration: calculatedDuration,
        startDate: item.startDate,
        endDate: item.endDate
      } : {
        ...itemWithPricing,
        ...typeProps
      };

      // Store currency code in localStorage if it's the first item and currency is valid
      if (prev.length === 0 && item.currencyCode && item.currencyCode !== 'N/A') {
        localStorage.setItem('currencyCode', item.currencyCode);
      }
      
      // Initialize global cart dates if this is the first item with dates
      if (prev.length === 0 && item.startDate && item.endDate) {
        try {
          // Parse the dates from the item (handle both moment and date-fns formats)
          let parsedStartDate: Date;
          let parsedEndDate: Date;
          
          if (typeof item.startDate === 'string') {
            parsedStartDate = new Date(item.startDate);
          } else {
            parsedStartDate = item.startDate as Date;
          }
          
          if (typeof item.endDate === 'string') {
            parsedEndDate = new Date(item.endDate);
          } else {
            parsedEndDate = item.endDate as Date;
          }
          
          // Only set if the dates are valid
          if (!isNaN(parsedStartDate.getTime()) && !isNaN(parsedEndDate.getTime())) {
            setStartDateState(parsedStartDate);
            setEndDateState(parsedEndDate);
            localStorage.setItem('cart_start_date', format(parsedStartDate, 'yyyy-MM-dd'));
            localStorage.setItem('cart_end_date', format(parsedEndDate, 'yyyy-MM-dd'));
            console.log('Initialized cart dates from first item:', {
              startDate: format(parsedStartDate, 'yyyy-MM-dd'),
              endDate: format(parsedEndDate, 'yyyy-MM-dd')
            });
          }
        } catch (error) {
          console.error('Error parsing dates from cart item:', error);
        }
      }
      
      console.log("addtocart function call from context:", prev, itemWithDefaults);


      return dedupeCartItems([...prev, itemWithDefaults]);
    });
  };

  const removeFromCart = (id: number | string) => {
    setCartItemsRaw((prev: CartItem[]) => prev.filter((item: CartItem) => item.id !== id.toString()));
    setCartCount(prev => prev - 1);
  };

  const clearCart = () => {
    setCartItems([]);
    setCartCount(0);
    // Explicitly clear dates when cart is cleared
    setStartDateState(null);
    setEndDateState(null);
    localStorage.removeItem('cart_start_date');
    localStorage.removeItem('cart_end_date');
  };

  const updateTaxInfo = (label: string, percent: number) => {
    setTaxLabel(label);
    setTaxPercent(percent);
    // Store tax info in localStorage for persistence
    localStorage.setItem('cartTaxInfo', JSON.stringify({ label, percent }));
  };

  // Cart country management functions
  const setCartCountry = (country: string) => {
    console.log("country--------------->", country);

    setCartCountryState(country);
    localStorage.setItem('cartCountry', country);
  };
  const setCartCountryId = (country: string) => {
    console.log("country--------------:", country, "cartCountryId:", cartCountryId);

    if (cartCountryId !== country) {
      setCartCountryStateId(country);
      localStorage.setItem('selectedCountryId', country);
    }
  }

  const clearCartCountry = () => {
    setCartCountryState('');
    setCartCountryStateId('');
    localStorage.removeItem('cartCountry');
    // Clear selectedCountryId to revert to sessionDefaultCountryId
    // localStorage.removeItem('selectedCountryId');
  };

  const hasCartCountry = () => {
    return cartCountry !== '' && cartItems.length > 0;
  };

  const isInCart = (id: number | string | undefined | null) => {
    if (id === undefined || id === null) return false;
    return cartItems.some(item => item.id === id.toString());
  };

  // Calculate total cost with custom campaign duration (for non-logged-in users)
  const calculateTotalWithDuration = (customDuration: number) => {
    return cartItems.reduce((sum, item) => {
      // Deals have fixed pricing - no duration calculation
      if (item.isDeal || item.type === "Deal") {
        return sum + (item.price / 100);
      }

      // For digital billboards, use perSpotPrice * customDuration * spots
      if (item?.type?.toLowerCase() === "digital") {
        const spotPrice = item?.perSpotPrice ?? (item?.price / 100);
        const spots = item.spots ?? 1;
        const isSpotBased = item?.isSpotBased || item?.priceFormat === 'spot-based';

        if (isSpotBased) {
          return sum + (spotPrice * spots * customDuration);
        } else {
          return sum + (spotPrice * customDuration);
        }
      }

      // For classic billboards, use perDayPrice * customDuration
      if (item?.type?.toLowerCase() === "classic") {
        const dayPrice = item?.perDayPrice ?? 0;
        return sum + (dayPrice * customDuration);
      }

      // For packages, use fixed package pricing (no duration multiplication)
      if (item?.type?.toLowerCase() === "package") {
        return sum + (item.price / 100);
      }

      // For other types, multiply by custom duration
      return sum + ((item.price / 100) * customDuration);
    }, 0);
  };

  const updateCartFromApi = (apiInventories: any[]) => {
    console.log("api inventories----------->", apiInventories);

    const transformedItems = apiInventories.map(inv => {
      console.log("inv apiventories------------->", inv?.cartItemCountry);
      setCartCountryState(inv?.cartItemCountry);
      const type = inv.billboardType;
      const status: "available" | "sold-out" = inv.availableBooking ? "available" : "sold-out";

      // Create base item with API validity dates for packages
      const baseItem = {
        id: inv.cartItemId,
        name: inv.billboardName,
        location: inv.billboardAddress || "",
        state: inv.billboardVenueType || "", // Assuming venueType is state
        price: inv.billboardPeriodSubTotalPrice * 100,
        impressions: inv.billboardPotentialViews || 0,
        uniqueVisitors: inv.billboardUniqueVisitors || 0,
        frequency: inv.billboardFrequency || 0,
        size: `${inv.billboardResolutionWidth || ''}x${inv.billboardResolutionHeight || ''}`,
        type: type,
        status,
        image: inv.billboardThumbnailUrl,
        specifications: {
          resolution: `${inv.billboardResolutionWidth || ''}x${inv.billboardResolutionHeight || ''}`,
          duration: inv.billboardType === "Classic" ? "30 days" : "Variable"
        },
        isDeal: type === "Deal",
        // Extract package validity dates from API
        packageStartDate: inv.validityStartDate || undefined,
        packageEndDate: inv.validityEndDate || undefined,
        priceFormat: 'spot-based' as 'spot-based' | 'day-based',
      };

      // Now get type properties with the actual item data
      const typeProps = getInventoryTypeProperties(type, baseItem);

      // Merge base item with type properties
      const itemWithTypeProps = {
        ...baseItem,
        ...typeProps
      };

      if (type?.toLowerCase() === "digital") {
        return {
          ...itemWithTypeProps,
          spots: 1,
          campaignDuration: 30,
          dailyHours: 18,
          daypartSchedules: ['Morning', 'Afternoon', 'Evening'],
          priceFormat: 'spot-based' as 'spot-based' | 'day-based',
        };
      } else if (type?.toLowerCase() === "classic") {
        return {
          ...itemWithTypeProps,
          campaignDuration: 30,
          priceFormat: 'spot-based' as 'spot-based' | 'day-based',
        };
      }
      return {
        ...itemWithTypeProps,
        priceFormat: 'spot-based' as 'spot-based' | 'day-based',
      };
    });
    console.log("update cart from api function called in context:", transformedItems);

    const deduped = dedupeCartItems(transformedItems);
    console.log("after deduped:", deduped);

    setCartItemsRaw(deduped);
    setCartCount(deduped.length);
  };

  const fetchCartDetails = async () => {
    if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
      try {
        const mockResponse = await generateMockData(GET_MY_CART_ITEMS);
        const data = mockResponse.data;

        if (data?.cartpageItem) {
          updateCartFromApi(data.cartpageItem);

          // Store a mock token for other parts of the application
          if (!localStorage.getItem("authToken")) {
            localStorage.setItem("authToken", "mock-auth-token");
            localStorage.setItem("userId", "mock-user-id");
            setIsAuthenticated(true);
          }
        }
      } catch (err) {
        console.error("Error generating mock data:", err);
      }
    } else {
      const token = localStorage.getItem("authToken");
      const userId = localStorage.getItem("userId");

      if (!token || !userId) {
        // clearCart();
        return;
      }

      // 1. Read URL params to check for draft/edit flow
      const urlParams = new URLSearchParams(window.location.search);
      const edit = urlParams.get('edit') === 'true';
      const draftId = urlParams.get('draftId');
      // Use selectedCampaignId as a fallback, as it's set in cart.tsx
      const campaignId = draftId || localStorage.getItem('selectedCampaignId');
      const isDraftFlow = (edit && campaignId);

      try {
        console.log("[DEBUG] edit flow:", {
          urlParams,
          edit,
          draftId,
          campaignId,
          isDraftFlow
        });

        if (isDraftFlow) {
          // 2. Draft Flow: Call CART_PAGE_ITEM
          console.log(`[CONTEXT] fetchCartDetails: Draft flow detected (campaignId: ${campaignId}, edit: ${edit}). Calling CART_PAGE_ITEM.`);
          const { data } = await client.query({
            query: CART_PAGE_ITEM,
            fetchPolicy: "network-only",
            variables: {
              accessToken: token,
              campaignId: campaignId || '',
              userId: userId,
              edit: edit,
              // Use context dates as they are the source of truth
              startDate: startDate ? format(startDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
              endDate: endDate ? format(endDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
              // --- START OF FIX: Use import.meta.env ---
              exposure: import.meta.env.VITE_APP_EXPOSURE
              // --- END OF FIX ---
            },
          });

          // Response is an array of campaigns, we get the first one
          const campaignData = data?.cartPageItem?.[0];
          if (campaignData && campaignData.campaignInventories) {
            // Update cart items from the draft's inventories
            updateCartFromApi(campaignData.campaignInventories);
            // Set count from draft inventories
            setCartCount(campaignData.campaignInventories.length);
          } else {
            // No draft data found, set count to 0
            setCartCount(0);
          }

        } else {
          const { data } = await client.query({
            query: GET_MY_CART_ITEMS,
            variables: { accessToken: token, userId },
            fetchPolicy: "network-only",
          });

          if (data?.cartpageItem) {
            updateCartFromApi(data.cartpageItem);
          }
        }
      } catch (error) {
        console.error("❌ Error fetching cart details:", error);
        clearCart();
      }
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    const userId = localStorage.getItem("userId");
    if ((token && userId) || import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      fetchCartDetails();
    }
  }, []);

  return (
    <CartContext.Provider value={{
      cartItems,
      addToCart,
      removeFromCart,
      clearCart,
      isInCart,
      cartCount,
      totalCost,
      totalImpressions,
      totalUniqueReach,
      totalAverageFrequency,
      updateSpots,
      updateCampaignSettings,
      updateCampaignDuration,
      updateDaypartSchedules,
      taxLabel,
      taxPercent,
      updateTaxInfo,
      requiresCampaignDates,
      getAutomaticCampaignDates,
      hasInventoryTypeMix,
      getInventoryTypeBreakdown,
      getInventoryTypeProperties,
      fetchCartDetails,
      setCartItems,
      updateCartFromApi,
      setCartItemCount: setCartCount,
      priceFormat,
      setPriceFormat,
      cartCountry,
      setCartCountry,
      cartCountryId,
      setCartCountryId,
      clearCartCountry,
      hasCartCountry,
      calculateTotalWithDuration,
      currentCampaignDuration,
      setCurrentCampaignDuration,
      startDate,
      endDate,
      updateStartDate,
      updateEndDate,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}